import type { ExamSet, LicenceClass, Question } from "./types.ts";
import type { LoadedCatalog } from "./catalog.ts";

/** Klasse B — the default licence class in the upstream app. */
export const DEFAULT_CLASS_ID = 6;

export interface ExamQuestionState {
  question: Question;
  /** Indices of selected answers, or the typed value for numeric questions. */
  selected: number[];
  typed: string;
  /** Whether the user has committed an answer for this question. */
  touched: boolean;
}

export interface ExamSession {
  set: ExamSet;
  licenceClass: LicenceClass;
  items: ExamQuestionState[];
  startedAt: number;
}

/**
 * Builds an exam session. Without `setId` an official sheet is picked at
 * random for the licence class; with one, that exact sheet is used.
 */
export function createExam(
  catalog: LoadedCatalog,
  classId: number = DEFAULT_CLASS_ID,
  setId?: number
): ExamSession {
  const sets = catalog.sets.filter((s) => s.classId === classId);
  const set =
    setId !== undefined
      ? catalog.sets.find((s) => s.id === setId)
      : sets[Math.floor(Math.random() * sets.length)];
  if (!set) throw new Error(`No exam set ${setId ?? ""} for class ${classId}`);

  const licenceClass =
    catalog.classes.find((c) => c.id === classId) ?? {
      id: classId, name: "?", fullname: "?", description: "", maxAllowedPoints: 10,
    };

  const items = set.questionIds
    .map((id) => catalog.byId.get(id))
    .filter((q): q is Question => Boolean(q))
    .map((question) => ({ question, selected: [], typed: "", touched: false }));

  return { set, licenceClass, items, startedAt: Date.now() };
}

/** A question is correct only when the selection matches the key exactly. */
export function isCorrect(item: ExamQuestionState): boolean {
  const q = item.question;
  if (q.kind === "number") {
    return normalizeNumber(item.typed) === normalizeNumber(q.numeric?.value ?? "");
  }
  const expected = q.answers.map((a, i) => (a.correct ? i : -1)).filter((i) => i >= 0);
  const got = [...item.selected].sort((a, b) => a - b);
  return (
    expected.length === got.length && expected.every((v, i) => v === got[i])
  );
}

function normalizeNumber(s: string): string {
  return s.trim().replace(",", ".").replace(/^0+(?=\d)/, "");
}

export interface ExamResult {
  passed: boolean;
  errorPoints: number;
  maxPoints: number;
  correctCount: number;
  total: number;
  /** Wrong answers worth 5 points; two of these fail the exam outright. */
  fivePointMistakes: number;
  failedOnPoints: boolean;
  failedOnFivePointRule: boolean;
  durationMs: number;
  wrong: ExamQuestionState[];
}

/**
 * German theory exam rules: you fail above the class's error-point budget
 * (10 for class B), or if you get two 5-point questions wrong.
 */
export function gradeExam(session: ExamSession): ExamResult {
  let errorPoints = 0;
  let correctCount = 0;
  let fivePointMistakes = 0;
  const wrong: ExamQuestionState[] = [];

  for (const item of session.items) {
    if (isCorrect(item)) {
      correctCount++;
      continue;
    }
    wrong.push(item);
    errorPoints += item.question.points;
    if (item.question.points === 5) fivePointMistakes++;
  }

  const maxPoints = session.licenceClass.maxAllowedPoints;
  const failedOnPoints = errorPoints > maxPoints;
  const failedOnFivePointRule = fivePointMistakes >= 2;

  return {
    passed: !failedOnPoints && !failedOnFivePointRule,
    errorPoints,
    maxPoints,
    correctCount,
    total: session.items.length,
    fivePointMistakes,
    failedOnPoints,
    failedOnFivePointRule,
    durationMs: Date.now() - session.startedAt,
    wrong,
  };
}
